"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useJobsModal } from "@/hooks/useJobsModal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";

type Job = {
  id: string;
  title: string;
  description: string;
  department: string | null;
  category: string | null;
  salaryRange: string | null;
  requirements: string | null;
  responsibilities: string | null;
  qualifications: string | null;
  contactInfo: string | null;
  applicationUrl: string | null;
  status: string;
  postedDate: Date;
  expirationDate: Date | null;
  featuredImageId: string | null;
  attachmentId: string | null;
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const job = row.original as Job;
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);
  const setJobId = useJobsModal((s) => s.setJobId);
  const setIsModalOpen = useJobsModal((s) => s.setIsModalOpen);
  const setDefaultValues = useJobsModal((s) => s.setDefaultValues);

  const handleStatusChange = (newStatus: "active" | "closed" | "draft") => {
    setJobId(job.id);
    setDefaultValues({
      title: job.title,
      description: job.description,
      department: job.department || "",
      category: job.category || "",
      salaryRange: job.salaryRange || "",
      requirements: job.requirements || "",
      responsibilities: job.responsibilities || "",
      qualifications: job.qualifications || "",
      contactInfo: job.contactInfo || "",
      applicationUrl: job.applicationUrl || "",
      status: newStatus,
      postedDate: new Date(job.postedDate),
      expirationDate: job.expirationDate
        ? new Date(job.expirationDate)
        : undefined,
      featuredImageId: job.featuredImageId || undefined,
      attachmentId: job.attachmentId || undefined,
    });
    setIsModalOpen(true);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onClick={() => {
            setJobId(job.id);
            setDefaultValues({
              title: job.title,
              description: job.description,
              department: job.department || "",
              category: job.category || "",
              salaryRange: job.salaryRange || "",
              requirements: job.requirements || "",
              responsibilities: job.responsibilities || "",
              qualifications: job.qualifications || "",
              contactInfo: job.contactInfo || "",
              applicationUrl: job.applicationUrl || "",
              status: job.status as "active" | "closed" | "draft",
              postedDate: new Date(job.postedDate),
              expirationDate: job.expirationDate
                ? new Date(job.expirationDate)
                : undefined,
              featuredImageId: job.featuredImageId || undefined,
              attachmentId: job.attachmentId || undefined,
            });
            setIsModalOpen(true);
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setJobId(null); // Clear ID to create duplicate
            setDefaultValues({
              title: `${job.title} (Copy)`,
              description: job.description,
              department: job.department || "",
              category: job.category || "",
              salaryRange: job.salaryRange || "",
              requirements: job.requirements || "",
              responsibilities: job.responsibilities || "",
              qualifications: job.qualifications || "",
              contactInfo: job.contactInfo || "",
              applicationUrl: job.applicationUrl || "",
              status: "draft",
              postedDate: new Date(),
              expirationDate: job.expirationDate
                ? new Date(job.expirationDate)
                : undefined,
              featuredImageId: job.featuredImageId || undefined,
              attachmentId: job.attachmentId || undefined,
            });
            setIsModalOpen(true);
          }}
        >
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {job.status === "active" && (
          <DropdownMenuItem onClick={() => handleStatusChange("closed")}>
            Mark as Closed
          </DropdownMenuItem>
        )}
        {job.status === "closed" && (
          <DropdownMenuItem onClick={() => handleStatusChange("active")}>
            Reopen Job
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setDeleteId(job.id);
          }}
        >
          Delete
          <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
